import { useEffect, useRef, useState, type RefObject } from 'react';
import {
  getNormalizedSteerPercent,
  readViewportWidth,
  smoothSteerTarget,
} from './windowResize';

export type WindowControlStatus = 'idle' | 'active';

export interface UseWindowResizeControlResult {
  status: WindowControlStatus;
  normalizedSteerPercent: number | null;
  steerYRef: RefObject<number | null>;
}

export function useWindowResizeControl(
  enabled: boolean,
): UseWindowResizeControlResult {
  const [status, setStatus] = useState<WindowControlStatus>('idle');
  const [normalizedSteerPercent, setNormalizedSteerPercent] = useState<
    number | null
  >(null);
  const steerYRef = useRef<number | null>(null);

  const baselineWidthRef = useRef<number | null>(null);

  useEffect(() => {
    const trackBaseline = () => {
      const width = readViewportWidth();
      baselineWidthRef.current = Math.max(baselineWidthRef.current ?? 0, width);
    };

    trackBaseline();
    window.addEventListener('resize', trackBaseline);
    window.visualViewport?.addEventListener('resize', trackBaseline);

    return () => {
      window.removeEventListener('resize', trackBaseline);
      window.visualViewport?.removeEventListener('resize', trackBaseline);
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      steerYRef.current = null;
      setNormalizedSteerPercent(null);
      setStatus('idle');
      return undefined;
    }

    setStatus('active');

    const update = () => {
      const baseline = baselineWidthRef.current;
      if (baseline === null) {
        return;
      }

      const width = readViewportWidth();
      setNormalizedSteerPercent(getNormalizedSteerPercent(width, baseline));
      steerYRef.current = smoothSteerTarget(steerYRef.current, width, baseline);
    };

    update();
    window.addEventListener('resize', update);
    window.visualViewport?.addEventListener('resize', update);

    return () => {
      window.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('resize', update);
      steerYRef.current = null;
    };
  }, [enabled]);

  return {
    status,
    normalizedSteerPercent,
    steerYRef,
  };
}
